import { toOurProductsPage } from '@/feature/product/services/product.routing'
import {
  ensureDecodedURIComponent,
  safeEncodeURIComponent,
  sanitizeDecodedURIComponent,
} from '@/lib/typescript/uriUtils'
import {
  collapseDoubleSlashes,
  ensureLeadingSlash,
  isAbsoluteHttpUrl,
} from '@/lib/utils/link-utils'

export type SanitizedSearchEntry = DecodedURIComponentString & {
  __sanitized: true
}

export function toSearchListPage(rawSearchEntry: DecodedURIComponentString): string {
  const sanitized = sanitizeDecodedURIComponent(rawSearchEntry)

  if (!sanitized) {
    return toOurProductsPage()
  }

  return `/search/?q=${safeEncodeURIComponent(sanitized)}`
}

export function toEmbedSearchListPage(rawSearchEntry: DecodedURIComponentString): string {
  return `/embed${toSearchListPage(rawSearchEntry)}`
}

/**
 * Go to the brand product list page.
 */
export function toBrandProductListPage(brand: string): string {
  return `/brand/${brand}`
}

/**
 * Go to the category product list page.
 */
export function toCategoryListPage(slug: string) {
  const base = toOurProductsPage()
  const normalizedSlug = ensureLeadingSlash(slug)

  return collapseDoubleSlashes(
    normalizedSlug.startsWith(base) ? normalizedSlug : `${base}${normalizedSlug}`,
  )
}

/**
 * Go to the event product list page.
 */
export function toEventProductListPage(slug: string) {
  return `/event/${slug}`.replace(/\/+/g, '/')
}

export function toEventLinkHref(link: string): string {
  return isAbsoluteHttpUrl(link) ? ensureDecodedURIComponent(link) : toEventProductListPage(link)
}

/**
 * Replace /event/slug URLs in HTML content with the proper routing URL.
 */
export function replaceEventUrlsInHtml(text: string | undefined) {
  if (!text) return text

  return text.replace(/\/event\/([a-zA-Z0-9_-]+)/g, (_, slug) => toEventProductListPage(slug))
}

/**
 * Builds a full category search URL with q, filter, and pagination
 */
export function buildSearchCategoryUrl(name: string, brandSlug: string): string | null {
  const sanitized = sanitizeDecodedURIComponent(name as DecodedURIComponentString)
  if (!sanitized) return null

  // Generate base URL and fix `q` encoding (replace %20 with +)
  const base = toSearchListPage(sanitized)
  const qParam = sanitized.replace(/\s+/g, '+')
  const urlWithFixedQ = base.replace(/q=([^&]*)/, `q=${qParam}`)

  // Filter param with lowercase + `+` instead of space
  const rawFilter = sanitized.toLowerCase().replace(/\s+/g, '+')

  const filterPrefix = encodeURIComponent('/$s=')
  const filterSuffix = encodeURIComponent(`/marque>{${brandSlug}}`)

  return `${urlWithFixedQ}&filter=${filterPrefix}${rawFilter}${filterSuffix}&p=1`
}
